import { HTMLAttributes } from "react";
import { v4 as uuidv4 } from "uuid";
import * as yup from "yup";
import { Input, InputProps, forwardRef } from "@chakra-ui/react";
import FormattedNumberInput, {
  NumberFormatPropsBase,
  NumberFormatValues,
} from "react-number-format";
import { gql } from "__generated__/gql";
import { Writeable } from "utils/typescriptUtilis";
import { PhoneNumberPatch } from "__generated__/gql/graphql";
import * as phoneNumberSchemaFields from "utils/schemas/phoneNumber";

export const phoneNumberFragment = gql(/* GraphQL */ `
  fragment PhoneNumberInputFields on PhoneNumber {
    phone
  }
`);

export const updateOrCreatePhoneNumberFragment = gql(/* GraphQL */ `
  fragment UpdateOrCreatePhoneNumber on Mutation {
    updateOrCreatePhoneNumber(
      input: {
        phoneNumberId: $phoneNumberId
        phoneNumberData: $phoneNumberData
      }
    ) @include(if: $isPhoneNumberUpdatedOrCreated) {
      phoneNumber {
        id
        ...PhoneNumberInputFields
      }
    }
  }
`);

export const phoneNumberFields = ["phone"] as const;

export type PhoneNumberFieldsetType = Writeable<
  Pick<PhoneNumberPatch, (typeof phoneNumberFields)[number]>
>;

export const phoneNumberFieldsetSchema: yup.ObjectSchema<PhoneNumberFieldsetType> =
  yup.object().shape({ phone: phoneNumberSchemaFields.phone });

type UpdateOrCreatePhoneNumberVariables = {
  isPhoneNumberUpdatedOrCreated: boolean;
  phoneNumberData?: PhoneNumberPatch;
  phoneNumberId: UUID | null;
};

export function getUpdateOrCreatePhoneNumberVariables(
  phoneNumberId: UUID | null | undefined,
  formData: PhoneNumberFieldsetType,
): UpdateOrCreatePhoneNumberVariables {
  if (!formData.phone) {
    return { phoneNumberId: null, isPhoneNumberUpdatedOrCreated: false };
  }
  return {
    isPhoneNumberUpdatedOrCreated: true,
    phoneNumberData: formData,
    phoneNumberId: phoneNumberId || uuidv4(),
  };
}

type Props = Omit<InputProps, "onChange" | "value"> &
  Omit<
    NumberFormatPropsBase<HTMLAttributes<HTMLInputElement>>,
    "onChange" | "value"
  > & {
    onChange?: (newValue: string) => void;
    shouldUseFormattedValue?: boolean;
    value?: string | null;
  };

const PhoneInput = forwardRef<Props, "input">(
  (
    { value, onChange, shouldUseFormattedValue, autoComplete = "off", ...rest },
    ref,
  ) => (
    <Input
      getInputRef={ref}
      as={FormattedNumberInput}
      type="tel"
      format="(###) ###-####"
      isNumericString
      value={value?.replace(/(^\+1|\D)/g, "") ?? ""}
      onValueChange={({
        formattedValue,
        value: newValue,
      }: NumberFormatValues) => {
        onChange?.(
          shouldUseFormattedValue
            ? formattedValue
            : newValue && `+1${newValue}`,
        );
      }}
      autoComplete={autoComplete}
      data-1p-ignore={autoComplete === "off" || undefined}
      data-lpignore={autoComplete === "off" || undefined}
      {...rest}
    />
  ),
);

export default PhoneInput;
